import { useEffect, useRef } from "react";
import { useApi } from "../../runtime/provider";
import type { EngineCheckoutSession } from "./engineSession";

/**
 * Gives the hold back when the checkout goes before paying — the sheet
 * closed or swiped away, back to the tickets — rather than when the
 * engine's 15 minutes run out (`accesses.releaseEngineCheckout`, which the
 * engine refuses for a session a payment has begun on). `releasable` says,
 * as the checkout goes, whether the hold is still the sheet's to give back:
 * not once paid or while money is moving, and not once the hosted checkout
 * has taken the session over (it gives the hold back itself). A StrictMode
 * remount is not leaving.
 */
export function useHoldRelease(
  session: EngineCheckoutSession,
  releasable: () => boolean,
) {
  const api = useApi();
  const latest = useRef(releasable);
  useEffect(() => {
    latest.current = releasable;
  });
  const mounted = useRef(0);
  useEffect(() => {
    mounted.current += 1;
    return () => {
      mounted.current -= 1;
      // Asked on the next turn: a remount in between is still here.
      setTimeout(() => {
        if (mounted.current > 0 || !latest.current()) return;
        void api.accesses.releaseEngineCheckout
          .mutate({
            accessId: session.accessId,
            clientSecret: session.clientSecret,
          })
          .catch(() => undefined);
      }, 0);
    };
  }, [api, session]);
}
